/**
 * X ADS › REPORTING — /x-ads/reporting
 *
 * Results from the CRM's reporting collection (filled by "Sync" and by the daily job): KPI
 * totals, a daily trend, a per-campaign / per-line-item table and each entity's daily
 * breakdown. Ranges reach 90 days back at most (X's limit for a sync). Days follow the ads
 * account's time zone.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDownToLine, BarChart3, ChevronRight, CircleDollarSign, Eye, Heart, Link as LinkIcon, MousePointerClick, PlayCircle, RefreshCw, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { RetryLine, XAdsNotice, XAdsShell } from '@/components/x-ads/XAdsShell';
import { XSelect } from '@/components/x-ads/XSelect';
import { useXConnection } from '@/lib/x-ads/connection';
import { addDays, dateTime, dayLabel, daysBetween, downloadCsv, lastDays, money, num, todayIn } from '@/lib/x-ads/format';
import { errMsg, xAdsService, type XAggregateRow, type XDailyRow, type XMetrics, type XReportLevel } from '@/services/x-ads.service';

type Metric = 'impressions' | 'engagements' | 'clicks' | 'urlClicks' | 'spend' | 'videoViews' | 'follows';
const METRICS: Array<{ key: Metric; name: string; Icon: typeof Eye }> = [
  { key: 'impressions', name: 'Impressions', Icon: Eye },
  { key: 'engagements', name: 'Engagements', Icon: Heart },
  { key: 'clicks', name: 'Clicks', Icon: MousePointerClick },
  { key: 'urlClicks', name: 'Link clicks', Icon: LinkIcon },
  { key: 'spend', name: 'Spend', Icon: CircleDollarSign },
  { key: 'videoViews', name: 'Video views', Icon: PlayCircle },
  { key: 'follows', name: 'Follows', Icon: UserPlus },
];
const PRESETS = [7, 14, 30, 90];
const n = (v: unknown) => Number(v) || 0;

const sum = (rows: XMetrics[]) => rows.reduce<Record<Metric, number>>((t, r) => {
  METRICS.forEach(({ key }) => { t[key] += n(r[key]); });
  return t;
}, { impressions: 0, engagements: 0, clicks: 0, urlClicks: 0, spend: 0, videoViews: 0, follows: 0 });

export default function XAdsReporting() {
  const { uid, account, reload } = useXConnection();
  const tz = account?.timezone;
  const currency = account?.currency ?? '';
  const today = todayIn(tz);
  const earliest = addDays(today, -89);
  const [range, setRange] = useState(() => lastDays(7, tz));
  const [level, setLevel] = useState<Extract<XReportLevel, 'campaign' | 'line_item'>>('campaign');
  const [rows, setRows] = useState<XAggregateRow[] | null>(null);
  const [trend, setTrend] = useState<XDailyRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [metric, setMetric] = useState<Metric>('impressions');
  const [drill, setDrill] = useState<XAggregateRow | null>(null);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true); setError(null);
    try {
      const [agg, series] = await Promise.all([xAdsService.aggregate(uid, { level, ...range }), xAdsService.trend(uid, { level, ...range })]);
      setRows(agg); setTrend(series);
    } catch (e) { setError(errMsg(e, 'Unable to load the report.')); }
    finally { setLoading(false); }
  }, [uid, level, range]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    const daysBack = Math.min(90, Math.max(1, daysBetween(range.from, today)));
    setSyncing(true);
    try {
      const r = await xAdsService.syncReport(uid, daysBack, level);
      toast.success(`Synced ${r.days} day${r.days === 1 ? '' : 's'} for ${r.entities} ${level === 'campaign' ? 'campaign' : 'line item'}${r.entities === 1 ? '' : 's'}`);
      reload();
      await load();
    } catch { /* toasted */ }
    finally { setSyncing(false); }
  };

  const setFrom = (v: string) => { if (v) setRange((r) => ({ from: v < earliest ? earliest : v, to: r.to < v ? v : r.to })); };
  const setTo = (v: string) => { if (v) setRange((r) => ({ from: r.from > v ? v : r.from, to: v > today ? today : v })); };
  const totals = useMemo(() => sum(rows ?? []), [rows]);
  const fmt = (key: Metric, v: number) => (key === 'spend' ? money(v, currency) : num(v));
  const active = METRICS.find((m) => m.key === metric)!;
  const entity = level === 'campaign' ? 'Campaign' : 'Line item';
  const presetDays = range.to === today ? daysBetween(range.from, range.to) : 0;

  const exportCsv = () => downloadCsv(`x-ads-${level}-${range.from}-${range.to}.csv`, [
    [entity, 'ID', ...METRICS.map((m) => (m.key === 'spend' ? `Spend (${currency})` : m.name))],
    ...(rows ?? []).map((r) => [r.entityName || r.entityId, r.entityId, ...METRICS.map((m) => n(r[m.key]))]),
  ]);

  return <XAdsShell section="Reporting" description="Spend, reach and engagement of your X campaigns."
    actions={<Button onClick={sync} loading={syncing} disabled={!uid} iconLeft={<RefreshCw size={15} />}>Sync</Button>}>
    {account?.sandbox && <XAdsNotice title="Sandbox account">Sandbox accounts have no real results — reports stay empty.</XAdsNotice>}
    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-text-muted">From<div className="mt-1 w-40"><Input type="date" min={earliest} max={today} value={range.from} onChange={(e) => setFrom(e.target.value)} /></div></label>
        <label className="text-xs text-text-muted">To<div className="mt-1 w-40"><Input type="date" min={earliest} max={today} value={range.to} onChange={(e) => setTo(e.target.value)} /></div></label>
        <div className="xa-subtabs !mb-0" role="group" aria-label="Quick ranges">
          {PRESETS.map((d) => <button key={d} type="button" aria-pressed={presetDays === d} onClick={() => setRange(lastDays(d, tz))}>{d} days</button>)}
        </div>
        <div className="w-40"><XSelect value={level} onChange={(v) => v && setLevel(v as typeof level)} options={[{ value: 'campaign', label: 'Campaigns' }, { value: 'line_item', label: 'Line items' }]} /></div>
      </div>
      <p className="xa-muted">{account?.lastSyncedAt ? `Last synced ${dateTime(account.lastSyncedAt)}` : 'Not synced yet'} · Days in {tz?.replace(/_/g, ' ') || 'UTC'}</p>
    </div>
    {error && <RetryLine message={error} onRetry={load} />}

    <div className="xa-stats">
      {METRICS.map(({ key, name, Icon }) => <button key={key} type="button" className="xa-stat" aria-pressed={metric === key} onClick={() => setMetric(key)}>
        <span className="xa-stat-label">{name}<Icon size={15} /></span>
        <span className="xa-stat-value">{rows ? fmt(key, totals[key]) : '—'}</span>
      </button>)}
    </div>

    <Card className="mb-4">
      <div className="mb-3"><h3 className="xa-panel-title">{active.name} by day</h3><p className="xa-muted">{range.from} → {range.to}</p></div>
      {loading && !rows ? <div className="skeleton h-48 rounded-xl" aria-label="Loading trend" />
        : trend.length > 1 ? <div className="h-56" role="img" aria-label={`${active.name} per day from ${range.from} to ${range.to}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend.map((t) => ({ date: t.date, value: n(t[metric]) }))} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <defs><linearGradient id="xa-trend-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.22} /><stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="date" tickFormatter={dayLabel} axisLine={false} tickLine={false} minTickGap={36} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
              <YAxis axisLine={false} tickLine={false} width={56} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
              <Tooltip contentStyle={{ borderRadius: 12, background: 'var(--color-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                labelFormatter={(v) => dayLabel(String(v))} formatter={(v: unknown) => [fmt(metric, Number(v)), active.name]} />
              <Area type="linear" dataKey="value" stroke="var(--color-primary)" strokeWidth={2.5} fill="url(#xa-trend-fill)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        : <div className="flex h-40 flex-col items-center justify-center rounded-xl border border-dashed border-border bg-surface-alt/30 text-center">
          <BarChart3 size={26} className="mb-2 text-text-muted" />
          <p className="text-sm font-medium">{trend.length === 1 ? 'One day of data so far' : 'No results in this range'}</p>
          <p className="xa-muted max-w-md">{trend.length === 1 ? 'The trend fills in as days accumulate.' : 'Press "Sync" to pull results from X. Paused campaigns and sandbox accounts have none.'}</p>
        </div>}
    </Card>

    <Card padded={false} className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
        <div><h3 className="xa-panel-title">By {entity.toLowerCase()}</h3><p className="xa-muted">Open a row for its daily breakdown.</p></div>
        <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!rows?.length} iconLeft={<ArrowDownToLine size={14} />}>Export CSV</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="xa-table">
          <caption className="sr-only">{entity} results from {range.from} to {range.to}</caption>
          <thead className="border-b border-border bg-surface-alt/60"><tr>
            <th scope="col">{entity}</th>
            {METRICS.map((m) => <th key={m.key} scope="col">{m.key === 'spend' ? `Spend${currency ? ` (${currency})` : ''}` : m.name}</th>)}
            <th scope="col">Eng. rate</th><th scope="col"><span className="sr-only">Daily</span></th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {(rows ?? []).map((r) => <tr key={r.entityId} className="hover:bg-surface-alt/40">
              <td><button type="button" className="max-w-[260px] truncate text-left font-semibold text-primary" onClick={() => setDrill(r)}>{r.entityName || r.entityId}</button><p className="text-[10px] text-text-muted">{r.entityId}</p></td>
              {METRICS.map((m) => <td key={m.key}>{fmt(m.key, n(r[m.key]))}</td>)}
              <td>{n(r.impressions) ? `${((n(r.engagements) / n(r.impressions)) * 100).toFixed(2)}%` : '—'}</td>
              <td><button type="button" className="p-1.5 text-primary" aria-label={`Daily breakdown for ${r.entityName || r.entityId}`} onClick={() => setDrill(r)}><ChevronRight size={16} /></button></td>
            </tr>)}
          </tbody>
        </table>
      </div>
      {rows?.length === 0 && <p className="p-8 text-center text-sm text-text-muted">No results in this range yet.</p>}
      {!rows && loading && <div className="skeleton m-4 h-24 rounded-xl" aria-label="Loading report" />}
    </Card>

    <DailyBreakdown uid={uid} level={level} range={range} row={drill} currency={currency} onClose={() => setDrill(null)} />
  </XAdsShell>;
}

function DailyBreakdown({ uid, level, range, row, currency, onClose }: {
  uid: string; level: XReportLevel; range: { from: string; to: string }; row: XAggregateRow | null; currency: string; onClose: () => void;
}) {
  const [days, setDays] = useState<XDailyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const id = row?.entityId;
  useEffect(() => {
    if (!id) return;
    let on = true;
    setDays(null); setError(null);
    xAdsService.bulkDaily(uid, [id], { level, ...range })
      .then((map) => { if (on) setDays(map[id] ?? []); })
      .catch((e) => { if (on) setError(errMsg(e)); });
    return () => { on = false; };
  }, [uid, id, level, range]);

  return <Modal open={!!row} onClose={onClose} title={`Daily results · ${row?.entityName || row?.entityId || ''}`} widthClass="max-w-4xl">
    {error && <XAdsNotice title="Could not load the breakdown" error>{error}</XAdsNotice>}
    {!days && !error && <div className="skeleton h-40 rounded-xl" aria-label="Loading daily results" />}
    {days?.length === 0 && <p className="xa-muted">No daily rows in this range.</p>}
    {!!days?.length && <div className="overflow-x-auto">
      <table className="xa-table">
        <thead className="border-b border-border bg-surface-alt/60"><tr><th scope="col">Day</th>{METRICS.map((m) => <th key={m.key} scope="col">{m.name}</th>)}</tr></thead>
        <tbody className="divide-y divide-border">
          {days.map((d) => <tr key={d.date}><td>{dayLabel(d.date)}</td>{METRICS.map((m) => <td key={m.key}>{m.key === 'spend' ? money(n(d.spend), currency) : num(n(d[m.key]))}</td>)}</tr>)}
        </tbody>
      </table>
    </div>}
  </Modal>;
}
